import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ImagePreview, { SelectedImage } from '../components/albums/ImagePreview';
import MasonryGrid from '../components/albums/MasonryGrid';
import Button from '../components/base/Button';
import Loading from '../components/base/Loading';
import PageHeader from '../components/base/PageHeader';
import { Role, useGetAlbumQuery, useGetImagesQuery } from '../graphql/schema';
import hasRole from '../helper/hasRole';

const itemsPerPage = 40;

const Album = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { slug, tagId } = useParams();
  const [searchParams] = useSearchParams();

  const [selectedImage, setSelectedImage] = useState<SelectedImage>();

  const { data, loading } = useGetAlbumQuery({
    variables: {
      slug: slug ?? '',
      password: searchParams.get('password'),
    },
  });

  const {
    data: imagesData,
    loading: imagesLoading,
    fetchMore,
    error,
  } = useGetImagesQuery({
    variables: {
      slug: slug ?? '',
      itemsPerPage,
      tagId,
      password: searchParams.get('password'),
    },
  });

  const handleNext = useCallback(async () => {
    if (!selectedImage || !imagesData) return;

    if (imagesData.images.items.length - 2 === selectedImage.index) {
      // Reached last image
      if (imagesData.images.nextToken) {
        await handleLoadMore();
      }
    }

    const img = imagesData.images.items[selectedImage.index + 1];
    if (img)
      setSelectedImage({
        ...img,
        index: selectedImage.index + 1,
      });
  }, [selectedImage, imagesData]);

  const handlePrev = useCallback(() => {
    if (!selectedImage) return;
    if (selectedImage.index === 0) return;

    const img = imagesData?.images.items[selectedImage.index - 1];
    if (img)
      setSelectedImage({
        ...img,
        index: selectedImage.index - 1,
      });
  }, [selectedImage, imagesData]);

  const handleLoadMore = useCallback(async () => {
    if (!imagesData?.images.nextToken) return;

    await fetchMore({
      variables: {
        slug: slug ?? '',
        itemsPerPage,
        tagId,
        startingToken: imagesData.images.nextToken,
      },
    });
  }, [imagesData, fetchMore, slug, tagId]);

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: !!imagesData?.images.nextToken,
    onLoadMore: handleLoadMore,
    // When there is an error, we stop infinite loading.
    // It can be reactivated by setting "error" state as undefined.
    disabled: !!error,
    // `rootMargin` is passed to `IntersectionObserver`.
    // We can use it to trigger 'onLoadMore' when the sentry comes near to become
    // visible, instead of becoming fully visible on the screen.
    rootMargin: '0px 0px 400px 0px',
  });

  if (loading || imagesLoading || !data?.album || !imagesData)
    return <Loading />;

  return (
    <div>
      <PageHeader title={data?.album.name}>
        {hasRole(user, [Role.USER, Role.ADMIN]) && (
          <Button
            label="Tags verwalten"
            onClick={() => navigate(`/albumTags/${data?.album.id}`)}
          />
        )}
      </PageHeader>

      <div className="flex items-center justify-center pb-4 flex-wrap">
        {[
          {
            id: '',
            name: 'Alle',
            priority: 999999,
          },
          ...(data?.album.tags ?? []),
          ...(data?.album.hasUnsortedImages
            ? [
                {
                  id: 'untagged',
                  name: 'Unsortiert',
                  priority: -1,
                },
              ]
            : []),
        ]
          .sort((a, b) => b.priority - a.priority)
          .map((tag) => (
            <button
              type="button"
              key={tag.id}
              className={`text-primary hover:text-white border border-primary  hover:bg-primary rounded-full text-base px-4 py-1 text-center me-3 mb-3 ${tag.id === tagId || (!tagId && tag.id === '') ? 'bg-primary text-white' : 'bg-white'}`}
              onClick={() =>
                navigate(
                  `/album/${slug}/${tag.id}${searchParams.get('password') ? `?password=${searchParams.get('password')}` : ''}`,
                )
              }
            >
              {tag.name}
            </button>
          ))}
      </div>

      <div className="mb-5">
        <MasonryGrid
          images={imagesData.images.items}
          setSelectedImage={setSelectedImage}
        />
      </div>

      {(imagesData?.images.nextToken || imagesLoading) && (
        <div
          className="w-full flex justify-center items-center py-5"
          ref={sentryRef}
        >
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}

      {selectedImage && (
        <ImagePreview
          image={selectedImage}
          onClose={() => setSelectedImage(undefined)}
          next={handleNext}
          prev={handlePrev}
          album={data.album}
          isLast={selectedImage.index === imagesData.images.items.length - 1}
        />
      )}
    </div>
  );
};

export default Album;
